import ButtonBase from '@ixigo/iui/ButtonBase';
import Typography from '@ixigo/iui/Typography';
import cn from '@repo/shared/utils/cn';

export interface SegmentedControlOption<Value extends string> {
	value: Value;
	label: string;
}

export interface SegmentedControlProps<Value extends string> {
	options: ReadonlyArray<SegmentedControlOption<Value>>;
	value: Value;
	onChange: (value: Value) => void;
	/** Accessible label for the group */
	'aria-label'?: string;
	className?: string;
}

/**
 * A pill-style segmented control with an animated sliding thumb.
 * Single-select between mutually exclusive options — exposes radiogroup
 * semantics (not tabs: it changes behavior, not visible panels).
 */
export function SegmentedControl<Value extends string>({
	options,
	value,
	onChange,
	className,
	'aria-label': ariaLabel,
}: SegmentedControlProps<Value>) {
	const selectedIndex = Math.max(
		0,
		options.findIndex((option) => option.value === value),
	);

	return (
		<div
			role="radiogroup"
			aria-label={ariaLabel}
			className={cn(
				'relative flex items-center rounded-full bg-tertiary p-5px',
				className,
			)}
		>
			{/* Sliding thumb */}
			<div
				aria-hidden
				className="absolute inset-y-5px rounded-full bg-primary shadow-100 transition-transform duration-300 ease-out"
				style={{
					width: `calc((100% - 10px) / ${options.length})`,
					transform: `translateX(${selectedIndex * 100}%)`,
				}}
			/>
			{options.map((option) => {
				const selected = option.value === value;
				return (
					// ButtonBase (not iui Button): Button forces a sized variant
					// (min-h-30px+, padding, color) that fights this custom pill +
					// sliding thumb. We only need its press/ripple/a11y base here.
					// biome-ignore lint/a11y/useSemanticElements: styled segmented control follows the ARIA radiogroup pattern; native radio inputs can't render the sliding-thumb design
					<ButtonBase
						key={option.value}
						role="radio"
						aria-checked={selected}
						// Don't fire onChange for the already-selected option —
						// callers treat onChange as a real selection change
						onClick={selected ? undefined : () => onChange(option.value)}
						// 28px segment + 5px container padding = 38px control, matching the
						// thumb's inset-y-5px so it sits flush. Off the spacing scale (no
						// scale token + body-md line-height yields exactly 38), so inline.
						// z-10 keeps the labels above the sliding thumb.
						style={{ height: 28 }}
						className="relative z-10 flex flex-1 items-center justify-center rounded-full outline-none"
						disableRipple
					>
						<Typography
							variant="body-md"
							className="whitespace-nowrap px-10 text-primary"
						>
							{option.label}
						</Typography>
					</ButtonBase>
				);
			})}
		</div>
	);
}
